import { Users, Briefcase, AlertTriangle, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useState } from "react";

function ArtistLoadRow({ artist }) {
  const statusColors = {
    overloaded: "bg-red-500",
    optimal: "bg-green-500",
    underutilized: "bg-blue-500"
  };

  return (
    <div className="flex items-center gap-3 py-2 border-b border-border/30 last:border-0">
      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: statusColors[artist.status]?.replace("bg-", "") || "#8A8783" }} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{artist.artist_name}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{artist.allocated_hours}h / {artist.available_hours}h</span>
          {artist.programs_count > 0 && <span>• {artist.programs_count} programs</span>}
        </div>
      </div>
      <div className="w-20">
        <Progress value={Math.min(artist.load_pct, 100)} className="h-1.5" />
        <p className={`text-xs text-right mt-0.5 ${artist.load_pct > 100 ? "text-red-500" : ""}`}>{artist.load_pct}%</p>
      </div>
      {artist.upcoming_leave && (
        <Badge variant="outline" className="text-xs">
          <Calendar className="w-3 h-3 mr-1" />
          Leave
        </Badge>
      )}
    </div>
  );
}

function PMLoadCard({ pm }) {
  return (
    <div className="p-3 rounded-lg border border-border/50 bg-card/50">
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-sm font-medium">{pm.pm_name}</p>
          <p className="text-xs text-muted-foreground">{pm.program_count} programs</p>
        </div>
        {pm.at_risk_count > 0 && (
          <Badge variant="destructive" className="text-xs">{pm.at_risk_count} at risk</Badge>
        )}
      </div>
      <div className="space-y-1">
        {pm.programs.slice(0, 3).map((prog, idx) => (
          <div key={idx} className="flex items-center gap-2 text-xs">
            <span className={`w-1.5 h-1.5 rounded-full ${prog.at_risk ? "bg-red-500" : "bg-muted-foreground"}`} />
            <span className="truncate flex-1">{prog.program_name}</span>
          </div>
        ))}
        {pm.program_count > 3 && <p className="text-xs text-muted-foreground">+{pm.program_count - 3} more</p>}
      </div>
    </div>
  );
}

function ConcentrationBar({ item }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="text-sm w-24 truncate">{item.work_type}</span>
      <div className="flex-1">
        <Progress value={item.concentration_pct} className={`h-2 ${item.risk_flag ? "[&>div]:bg-amber-500" : ""}`} />
      </div>
      <span className="text-sm font-mono w-16 text-right">{item.concentration_pct}%</span>
      {item.risk_flag && <AlertTriangle className="w-4 h-4 text-amber-500" />}
    </div>
  );
}

export default function PressureMap({ data, loading }) {
  const [tab, setTab] = useState("artists");

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardHeader><CardTitle className="h-6 bg-muted rounded w-48" /></CardHeader>
        <CardContent className="h-80" />
      </Card>
    );
  }

  if (!data) return null;

  const { artist_load, pm_load, work_type_concentration, thresholds, period } = data;

  const overloaded = artist_load.filter(a => a.status === "overloaded").length;
  const underutil = artist_load.filter(a => a.status === "underutilized").length;

  return (
    <Card data-testid="pressure-map-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg" style={{ fontFamily: 'var(--font-sans)' }}>Production Pressure</CardTitle>
          <div className="flex gap-2">
            {overloaded > 0 && <Badge variant="destructive">{overloaded} overloaded</Badge>}
            {underutil > 0 && <Badge variant="secondary">{underutil} underutil</Badge>}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{period?.start} to {period?.end}</p>
      </CardHeader>
      <CardContent>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full mb-4">
            <TabsTrigger value="artists" className="flex-1"><Users className="w-4 h-4 mr-2" />Artists</TabsTrigger>
            <TabsTrigger value="pms" className="flex-1"><Briefcase className="w-4 h-4 mr-2" />PMs</TabsTrigger>
            <TabsTrigger value="work" className="flex-1">Work Types</TabsTrigger>
          </TabsList>

          <TabsContent value="artists" className="mt-0">
            <div className="max-h-[350px] overflow-y-auto pr-1">
              {artist_load.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No artist data</p>
              ) : (
                artist_load.map((artist) => (
                  <ArtistLoadRow key={artist.artist_id} artist={artist} />
                ))
              )}
            </div>
            <div className="mt-3 pt-3 border-t flex gap-4 text-xs text-muted-foreground">
              <span>Overload: &gt;{thresholds?.overload_pct}%</span>
              <span>Underutil: &lt;{thresholds?.underutilized_pct}%</span>
            </div>
          </TabsContent>

          <TabsContent value="pms" className="mt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[350px] overflow-y-auto pr-1">
              {pm_load.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 col-span-2">No PM data</p>
              ) : (
                pm_load.map((pm, idx) => (
                  <PMLoadCard key={idx} pm={pm} />
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="work" className="mt-0">
            <div className="space-y-1">
              {work_type_concentration.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No work type data</p>
              ) : (
                work_type_concentration.map((item, idx) => (
                  <ConcentrationBar key={idx} item={item} />
                ))
              )}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Risk flag when concentration &gt;{thresholds?.concentration_risk_pct}%
            </p>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
